import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import heroImage from '../assets/r11.webp';
import claimImage from '../assets/r10.png';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' }
  })
};

const PatientIntake = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>AI Patient Intake & Payment Reminders | MDeRCM</title>
        <meta 
          name="description" 
          content="AI-powered patient intake and payment reminder services. Automate collections, reduce bad debt and improve patient payment rates by 40%." 
        />
        <meta 
          name="keywords" 
          content="patient payment reminders, healthcare revenue cycle management services, medical revenue cycle management services, revenue cycle management consulting services, medical billing outsourcing services, best revenue cycle management companies, revenue cycle management services usa, hospital revenue cycle management, healthcare cost reduction strategies, how to improve revenue cycle management, outsourced revenue cycle management services, revenue cycle management services for hospitals" 
        />
        <link rel="canonical" href="https://mdercm.com/healthcare/ai/patient-intake" />
        <meta property="og:title" content="AI Payment Reminders & Patient Collections | Healthcare Revenue Cycle Management" />
        <meta property="og:description" content="Automate patient payment reminders with AI. Increase on-time payments by 90% with intelligent collections automation." />
        <meta property="og:url" content="https://mdercm.com/healthcare/ai/patient-intake" />
        <meta property="og:type" content="website" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* BREADCRUMB NAVIGATION */}
      <div style={{ 
        padding: '20px 5%', 
        backgroundColor: '#f7f9fb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '8px',
          fontSize: '14px',
          color: '#666'
        }}>
          <Link 
            to="/" 
            style={{ 
              color: '#666', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#F26522'}
            onMouseOut={(e) => e.target.style.color = '#666'}
          >
            Home
          </Link>
          <span style={{ color: '#999' }}>/</span>
          <Link 
            to="/healthcare/ai" 
            style={{ 
              color: '#666', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#F26522'}
            onMouseOut={(e) => e.target.style.color = '#666'}
          >
            AI Solutions
          </Link>
          <span style={{ color: '#999' }}>/</span>
          <span style={{ color: '#F26522', fontWeight: '600' }}>Patient Intake AI Agent</span>
        </div>
      </div>

      {/* HERO SECTION */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '650px', zIndex: 2 }}>
          <motion.h1 style={{ fontSize: 'clamp(24px, 5vw, 42px)', fontWeight: 700, marginBottom: '20px', lineHeight: 1.4 }}>
            Medical Revenue Cycle Management Services - AI Payment Reminders & Patient Collections
          </motion.h1>
          <motion.p style={{ fontSize: 'clamp(14px, 2vw, 18px)', marginBottom: '30px', color: '#dddddd' }}>
            Leading <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</Link> offering AI-powered payment reminders. Our <strong>healthcare revenue cycle management services</strong> send smart nudges via SMS, email, or voice to gently remind patients of outstanding balances—leading to faster payments and implementing proven <strong>healthcare cost reduction strategies</strong>.
          </motion.p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {[
              ['90%', 'Increase in On-Time Payments'],
              ['85%', 'Boost in Patient Response Rates'],
              ['80%', 'Reduction in Overdue Balances']
            ].map(([value, label], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 160px',
                  maxWidth: '220px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#F26522' }}>{value}</div>
                <div style={{ fontSize: '16px', whiteSpace: 'pre-line', color: '#ffffff' }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* EXPLANATION SECTION */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
          <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '30px'
        }}>
          Best Revenue Cycle Management Companies - AI Payment Reminders Agent
        </h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '18px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, we provide comprehensive <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>revenue cycle management consulting services</Link> that include AI-powered patient payment automation and <strong>medical billing outsourcing services</strong>.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px'
        }}>
          <div style={{ flex: '1 1 500px', minWidth: '280px', maxWidth: '600px' }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#001F2B' }}>
              Hospital Revenue Cycle Management - Personalized Alerts, Delivered Automatically
            </h3>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link>'s AI Agent syncs with your billing system to send reminders based on payment status and timing. Our <strong>outsourced revenue cycle management services</strong> eliminate manual follow-ups—patients receive clear, friendly updates and direct links to pay, improving your collections with zero friction.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              Our <strong>revenue cycle management services for hospitals</strong> implement proven <strong>healthcare cost reduction strategies</strong> by automating patient communications. Learn <strong>how to improve revenue cycle management</strong> with intelligent payment reminders that increase on-time payments by 90%.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>MDeRCM</Link>'s <strong>medical billing and coding outsourcing</strong> services work seamlessly with our payment reminder system to create a comprehensive solution that reduces overdue balances and improves patient satisfaction.
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            style={{
              flex: '1 1 400px',
              minWidth: '280px',
              maxWidth: '500px',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
            }}
          >
            <img 
              src={claimImage} 
              alt="AI Payment Reminders Dashboard" 
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* HOW IT WORKS SECTION */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          How Our Healthcare Revenue Cycle Management Services Work
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '850px',
          margin: '0 auto 60px'
        }}>
          Our <strong>medical revenue cycle management services</strong> leverage AI to automate patient payment reminders, ensuring timely collections and improved cash flow.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '30px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[
            {
              step: '01',
              title: 'System Integration',
              desc: 'Seamlessly connects with your EHR and billing systems to access patient account data in real-time.',
              icon: '🔌'
            },
            {
              step: '02',
              title: 'Smart Analysis',
              desc: 'AI analyzes payment history, balance age, and patient preferences to determine optimal reminder timing.',
              icon: '🧠'
            },
            {
              step: '03',
              title: 'Personalized Messaging',
              desc: 'Generates customized reminder messages via SMS, email, or voice based on patient communication preferences.',
              icon: '💬'
            },
            {
              step: '04',
              title: 'Automated Follow-up',
              desc: 'Sends intelligent follow-up reminders at strategic intervals until payment is received or resolved.',
              icon: '🔄'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '35px 25px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                boxShadow: '0 8px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                fontSize: '14px',
                fontWeight: '700',
                color: '#F26522',
                backgroundColor: '#FFF5F0',
                padding: '5px 12px',
                borderRadius: '20px'
              }}>
                {item.step}
              </div>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{item.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {item.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* KEY FEATURES SECTION - 4x4 GRID */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Key Features of Our Payment Reminder System
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Comprehensive tools designed to maximize patient collections and streamline your revenue cycle.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '📱',
              title: 'Multi-Channel Delivery',
              desc: 'Send reminders via SMS, email, voice calls, or patient portal messages based on preferences.'
            },
            {
              icon: '🎯',
              title: 'Smart Timing',
              desc: 'AI determines the best time to send reminders based on patient behavior and response patterns.'
            },
            {
              icon: '💳',
              title: 'Easy Payment Links',
              desc: 'Include secure payment links in all reminders for one-click payment processing.'
            },
            {
              icon: '📊',
              title: 'Real-Time Analytics',
              desc: 'Track payment rates, response times, and collection metrics with detailed dashboards.'
            },
            {
              icon: '🔐',
              title: 'HIPAA Compliant',
              desc: 'All communications are fully encrypted and compliant with healthcare privacy regulations.'
            },
            {
              icon: '🌐',
              title: 'Multilingual Support',
              desc: 'Send reminders in multiple languages to accommodate diverse patient populations.'
            },
            {
              icon: '⚙️',
              title: 'Customizable Templates',
              desc: 'Create branded message templates that match your practice\'s tone and style.'
            },
            {
              icon: '🤝',
              title: 'Payment Plan Integration',
              desc: 'Automatically offer payment plan options for high-balance accounts.'
            },
            {
              icon: '📈',
              title: 'Performance Tracking',
              desc: 'Monitor collection rates and identify trends to optimize reminder strategies.'
            },
            {
              icon: '🔔',
              title: 'Automated Escalation',
              desc: 'Gradually increase reminder frequency for overdue accounts with smart escalation rules.'
            },
            {
              icon: '💼',
              title: 'Staff Productivity',
              desc: 'Free up staff time by eliminating manual call campaigns and follow-ups.'
            },
            {
              icon: '✅',
              title: 'Compliance Monitoring',
              desc: 'Ensure all reminders comply with TCPA, FDCPA, and state-specific regulations.'
            },
            {
              icon: '🎨',
              title: 'Brand Consistency',
              desc: 'Maintain your practice branding across all patient payment communications.'
            },
            {
              icon: '📞',
              title: 'Two-Way Communication',
              desc: 'Allow patients to respond to reminders with questions or payment confirmations.'
            },
            {
              icon: '⏰',
              title: 'Schedule Flexibility',
              desc: 'Set specific days and times for reminder delivery to avoid patient disruption.'
            }
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '30px 20px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '2px solid #e5e7eb',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                backgroundColor: '#ffffff',
                boxShadow: '0 8px 25px rgba(242, 101, 34, 0.15)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '15px' }}>{feature.icon}</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '10px' 
              }}>
                {feature.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* BENEFITS SECTION */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Benefits of AI-Powered Payment Reminders
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '850px',
          margin: '0 auto 60px'
        }}>
          Transform your patient collections with automated, intelligent payment reminders that drive results.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '30px',
          maxWidth: '1300px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '💰',
              title: 'Faster Payments',
              desc: 'Reduce average collection time by 60% with timely, automated reminders that keep balances top of mind.'
            },
            {
              icon: '📉',
              title: 'Lower Bad Debt',
              desc: 'Decrease write-offs by 75% through consistent follow-up and early intervention on overdue accounts.'
            },
            {
              icon: '😊',
              title: 'Better Patient Experience',
              desc: 'Provide convenient payment options and friendly reminders that patients appreciate.'
            },
            {
              icon: '⚡',
              title: 'Reduced Staff Workload',
              desc: 'Eliminate hours of manual calling and follow-up, allowing staff to focus on complex cases.'
            },
            {
              icon: '📱',
              title: 'Higher Response Rates',
              desc: 'Achieve 85% higher response rates with personalized, multi-channel communication strategies.'
            },
            {
              icon: '🎯',
              title: 'Improved Cash Flow',
              desc: 'Accelerate revenue collection and maintain healthier cash flow with automated payment tracking.'
            },
            {
              icon: '🔍',
              title: 'Better Insights',
              desc: 'Gain visibility into payment patterns and collection effectiveness with comprehensive analytics.'
            },
            {
              icon: '🛡️',
              title: 'Compliance Assurance',
              desc: 'Maintain regulatory compliance with built-in HIPAA, TCPA, and FDCPA protections.'
            }
          ].map((benefit, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '35px 25px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                boxShadow: '0 8px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{benefit.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {benefit.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {benefit.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* RELATED SERVICES - 4x4 GRID */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Complete Revenue Cycle Management Services
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Explore our comprehensive suite of AI-powered healthcare revenue cycle management solutions.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '✅',
              title: 'Eligibility Check',
              desc: 'Real-time insurance verification and eligibility checks to prevent claim denials.',
              link: '/healthcare/ai/eligibility-check'
            },
            {
              icon: '📋',
              title: 'Policy Status Verification',
              desc: 'Automated verification of patient insurance policy status and coverage details.',
              link: '/healthcare/ai/policy-status-verification'
            },
            {
              icon: '📝',
              title: 'Prior Authorization',
              desc: 'Streamlined prior authorization process with automated submission and tracking.',
              link: '/healthcare/ai/prior-authorization'
            },
            {
              icon: '💲',
              title: 'Insurance Contract Repricing',
              desc: 'Intelligent contract repricing to maximize reimbursements from insurance payers.',
              link: '/healthcare/ai/InsuranceContract-Repricing'
            },
            {
              icon: '❌',
              title: 'Denial Management',
              desc: 'AI-powered denial prevention, analysis, and appeals to recover lost revenue.',
              link: '/healthcare/ai/denial-management'
            },
            {
              icon: '💳',
              title: 'Payment Posting',
              desc: 'Automated payment posting with accurate reconciliation and reporting.',
              link: '/healthcare/ai/payment-posting'
            },
            {
              icon: '📊',
              title: 'Accounts Receivable',
              desc: 'Comprehensive AR management with automated follow-up and collections.',
              link: '/healthcare/ai/accounts-receivable'
            },
            {
              icon: '🛡️',
              title: 'Compliance Agent',
              desc: 'Ensure HIPAA compliance and regulatory adherence across all operations.',
              link: '/healthcare/ai/compliance-agent'
            },
            {
              icon: '🦷',
              title: 'Dental RCM',
              desc: 'Specialized revenue cycle management solutions for dental practices.',
              link: '/Dental'
            },
            {
              icon: '🧠',
              title: 'Mental Health RCM',
              desc: 'Tailored billing and revenue cycle services for mental health providers.',
              link: '/MentalHealth'
            },
            {
              icon: '📑',
              title: 'Compliance Services',
              desc: 'Complete compliance management and risk assessment services.',
              link: '/compliance'
            },
            {
              icon: '📰',
              title: 'Industry News & Insights',
              desc: 'Stay updated with latest healthcare RCM trends and best practices.',
              link: '/blog'
            },
            {
              icon: '🎓',
              title: 'Denial Code Resources',
              desc: 'Comprehensive guide to insurance denial codes and resolution strategies.',
              link: '/resources/denial-codes'
            },
            {
              icon: '📚',
              title: 'RCM Resources',
              desc: 'Educational resources and tools for healthcare revenue cycle optimization.',
              link: '/resources'
            },
            {
              icon: '🎯',
              title: 'Why Choose Us',
              desc: 'Discover why healthcare providers trust MDeRCM for their revenue cycle needs.',
              link: '/why'
            }
          ].map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03, duration: 0.5 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '30px 20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '250px',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                backgroundColor: '#ffffff',
                boxShadow: '0 8px 25px rgba(242, 101, 34, 0.15)',
                transform: 'translateY(-5px)'
              }}
            >
              <div>
                <div style={{ fontSize: '42px', marginBottom: '15px' }}>{service.icon}</div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#001F2B', 
                  marginBottom: '12px' 
                }}>
                  {service.title}
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  lineHeight: '1.6',
                  marginBottom: '15px'
                }}>
                  {service.desc}
                </p>
              </div>
              <Link 
                to={service.link}
                style={{ 
                  color: '#F26522', 
                  textDecoration: 'none', 
                  fontWeight: '600',
                  fontSize: '15px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                Learn More →
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE MDERCM - 4x4 GRID */}
      <div style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Why Choose MDeRCM for Outsourced Revenue Cycle Management Services?
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> provides industry-leading <strong>revenue cycle management services USA</strong> with proven results and dedicated support.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '🏆',
              title: 'Industry-Leading Expertise',
              desc: 'Over 10+ years of experience in healthcare revenue cycle management with proven track record of success.'
            },
            {
              icon: '🤖',
              title: 'Advanced AI Technology',
              desc: 'Cutting-edge AI and machine learning algorithms that continuously improve accuracy and efficiency.'
            },
            {
              icon: '💵',
              title: 'Maximize Revenue',
              desc: 'Increase revenue by up to 30% through optimized collections, reduced denials, and faster payments.'
            },
            {
              icon: '⚡',
              title: '24/7 Support',
              desc: 'Round-the-clock dedicated support team ensuring seamless operations and quick issue resolution.'
            },
            {
              icon: '🔒',
              title: 'HIPAA Compliant',
              desc: 'Fully compliant with HIPAA and industry regulations, ensuring data security and patient privacy.'
            },
            {
              icon: '📈',
              title: 'Scalable Solutions',
              desc: 'Flexible services that grow with your practice, from small clinics to large hospital systems.'
            },
            {
              icon: '💡',
              title: 'Transparent Reporting',
              desc: 'Real-time dashboards and detailed analytics providing complete visibility into your revenue cycle.'
            },
            {
              icon: '🎯',
              title: 'Proven ROI',
              desc: 'Measurable results with most clients seeing positive ROI within 3-6 months of implementation.'
            },
            {
              icon: '🌟',
              title: 'Client Satisfaction',
              desc: '98% client satisfaction rate with dedicated account managers and personalized service.'
            },
            {
              icon: '🔄',
              title: 'Seamless Integration',
              desc: 'Easy integration with all major EHR systems and practice management software.'
            },
            {
              icon: '💼',
              title: 'Expert Team',
              desc: 'Certified medical billing specialists and revenue cycle experts managing your accounts.'
            },
            {
              icon: '🎓',
              title: 'Continuous Training',
              desc: 'Regular staff training on latest billing codes, compliance updates, and best practices.'
            },
            {
              icon: '🏥',
              title: 'Multi-Specialty Expertise',
              desc: 'Experience across 50+ medical specialties including dental, mental health, and more.'
            },
            {
              icon: '📞',
              title: 'Dedicated Account Manager',
              desc: 'Personal account manager assigned to oversee your revenue cycle operations.'
            },
            {
              icon: '🚀',
              title: 'Fast Implementation',
              desc: 'Quick onboarding process with go-live in as little as 2-4 weeks.'
            }
          ].map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '25px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '12px' }}>{reason.icon}</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '10px' 
              }}>
                {reason.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {reason.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '60px'
        }}>
          <Link 
            to="/free-trial"
            style={{
              backgroundColor: '#F26522',
              color: '#ffffff',
              padding: '18px 50px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '18px',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#D94F1C';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(242, 101, 34, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#F26522';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 6px 20px rgba(242, 101, 34, 0.4)';
            }}
          >
            Start Your Free Trial Today
          </Link>
        </div>
      </div>

      <FAQSection />
      <ConsultationCTA />
    </>
  );
};

export default PatientIntake;